import { useState } from "react";
import { toast } from "react-toastify";

import {
  createWorkflowOrLocalAction,
  GraphDetails,
  InputParameter,
  Workflow,
} from "../api";
import { HTTP_STATUS } from "../constants/http-status";
import { updateGraphContent } from "../stores/graph/content.ts";
import { $workflowsByGraphID } from "../stores/workflows.ts";

// Helper function to handle workflow results
const handleWorkflowResult = (
  result: {
    data: unknown;
    error: unknown;
    response: { status: number };
  },
  actionId: string,
  failedActions: Array<{ actionId: string; error: string }>,
  results: unknown[],
): void => {
  if (!result.data || result.error) {
    failedActions.push({
      actionId,
      error:
        result.error instanceof Error ? result.error.message : "Unknown error",
    });
    return;
  }

  const { status } = result.response;
  if (status === HTTP_STATUS.OK) {
    updateGraphContent(result.data as GraphDetails);
    toast.success("Data transform succeeded");
  } else if (status === HTTP_STATUS.CREATED) {
    const workflow = result.data as Workflow;

    // Ensure the workflow has the correct status and timestamp
    const workflowWithStatus: Workflow = {
      ...workflow,
      status: workflow.status || "RUNNING",
      timestamp: workflow.timestamp || new Date(),
    };

    results.push(result);

    // Add the workflow to the workflows store so it can be tracked for polling
    const currentWorkflows = $workflowsByGraphID.get().data || [];
    const updatedWorkflows = [workflowWithStatus, ...currentWorkflows];

    $workflowsByGraphID.set({
      data: updatedWorkflows,
      error: $workflowsByGraphID.get().error,
      loading: $workflowsByGraphID.get().loading,
    });
  }
};

/**
 * Custom hook to handle workflow execution logic
 */
export const useWorkflowExecution = (): {
  executeMultipleWorkflows: (props: {
    graphId: string;
    inputParameters: { [actionId: string]: Array<InputParameter> };
    invalidateStore: boolean;
    nodeId: string;
    onComplete?: () => void;
    queryActions: Array<{ id: string }>;
    selectedRelationships?: Record<string, string[]>;
  }) => Promise<void>;
  isRunning: boolean;
} => {
  const [isRunning, setIsRunning] = useState(false);

  const executeMultipleWorkflows = async ({
    graphId,
    inputParameters,
    nodeId,
    onComplete,
    queryActions,
    selectedRelationships,
  }: {
    graphId: string;
    inputParameters: { [actionId: string]: Array<InputParameter> };
    invalidateStore?: boolean;
    nodeId: string;
    onComplete?: () => void;
    queryActions: Array<{ id: string }>;
    selectedRelationships?: Record<string, string[]>;
  }): Promise<void> => {
    try {
      setIsRunning(true);

      const results: Array<{
        data: unknown;
        error: unknown;
        response: { status: number };
      }> = [];
      const failedActions: Array<{ actionId: string; error: string }> = [];

      for (let i = 0; i < queryActions.length; i++) {
        const action = queryActions[i];

        try {
          const outputMappingIds = selectedRelationships?.[action.id];
          if (!outputMappingIds || outputMappingIds.length === 0) {
            toast.warn(
              `No selected relationships for action ${action.id}, skipping.`,
            );
            continue; // skip to next action
          }

          const result = await createWorkflowOrLocalAction({
            body: {
              graphId,
              nodeId,
              queryAction: {
                id: action.id,
                inputParameters: inputParameters[action.id] || [],
                selectedOutputMappingIds: outputMappingIds,
              },
            },
          });

          handleWorkflowResult(result, action.id, failedActions, results);
        } catch (error) {
          failedActions.push({
            actionId: action.id,
            error: error instanceof Error ? error.message : "Unknown error",
          });
          toast.error(
            `Failed to run: ${error instanceof Error ? error.message : "Unknown error"}`,
          );
        }
      }

      if (failedActions.length > 0) {
        toast.warn(
          `${failedActions.length} out of ${queryActions.length} actions failed.`,
        );
      }

      if (onComplete) onComplete();
    } catch (error) {
      toast.error(`Failed to run Query Actions`);
      throw error;
    } finally {
      setIsRunning(false);
    }
  };

  return {
    executeMultipleWorkflows,
    isRunning,
  };
};
